import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

/** Popularne miasta — klik → lista z filtrem (?city=) */
const POPULAR_CITIES = [
  'Warszawa',
  'Kraków',
  'Wrocław',
  'Poznań',
  'Gdańsk',
  'Gdynia',
  'Łódź',
  'Szczecin',
  'Lublin',
  'Katowice',
  'Bydgoszcz',
  'Białystok',
  'Olsztyn',
  'Toruń',
  'Rzeszów',
  'Kielce',
];

const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cityQuery, setCityQuery] = useState('');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'iKominiarz.pl',
    description: 'Platforma do szybkiego znalezienia sprawdzonego kominiarza w Polsce',
    url: 'https://ikominiarz.pl',
    logo: 'https://ikominiarz.pl/logo.png',
    image: 'https://ikominiarz.pl/chimney-hero.png',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'PL',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Poland',
    },
    serviceType: 'Usługi kominiarskie',
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = cityQuery.trim();
    navigate(q ? `/chimneys?city=${encodeURIComponent(q)}` : '/chimneys');
  };

  return (
    <Layout hideFooter={false}>
      <SEO
        title="iKominiarz.pl – Znajdź sprawdzonego kominiarza w Polsce"
        description="iKominiarz.pl to platforma do szybkiego znalezienia sprawdzonego kominiarza, umawiania wizyt, kontaktu i opinii. Zweryfikowani specjaliści z całej Polski."
        keywords="kominiarz, usługi kominiarskie, czyszczenie komina, przegląd kominiarski, znaleźć kominiarza"
        url="https://ikominiarz.pl"
        structuredData={structuredData}
      />

      {/* Hero – one composition, brand-first, full-bleed */}
      <section className="relative min-h-[88vh] overflow-hidden bg-soot-950 text-white">
        <div className="absolute inset-0">
          <img
            src="/chimney-hero.png"
            alt=""
            className="h-full w-full object-cover animate-scale-in"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soot-950/95 via-soot-950/75 to-soot-950/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-soot-950/80 via-transparent to-soot-950/30" />
        </div>

        <div className="ik-container relative z-10 flex min-h-[88vh] flex-col justify-center py-16 md:py-24">
          <div className="max-w-2xl">
            <p className="animate-fade-up font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl">
              iKominiarz
            </p>
            <h1 className="animate-fade-up-delay mt-4 font-display text-3xl font-semibold leading-tight text-white sm:text-4xl md:text-5xl">
              Znajdź sprawdzonego kominiarza w swojej okolicy
            </h1>
            <p className="animate-fade-up-delay-2 mt-4 max-w-xl text-base leading-relaxed text-soot-200 md:text-lg">
              Umów przegląd online, pilnuj terminów pod ubezpieczenie i wybieraj fachowców z opiniami.
            </p>

            <form
              onSubmit={handleSearch}
              className="animate-fade-up-delay-2 mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
            >
              <label className="sr-only" htmlFor="city-search">
                Miasto lub kod pocztowy
              </label>
              <input
                id="city-search"
                type="text"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                placeholder="Miasto lub kod pocztowy"
                className="w-full rounded-md border-0 bg-white px-4 py-3 text-soot-900 shadow-soft placeholder:text-soot-400 focus:outline-none focus:ring-2 focus:ring-ember-500"
              />
              <button type="submit" className="ik-btn-primary whitespace-nowrap sm:px-8">
                Szukaj
              </button>
            </form>

            <div className="animate-fade-up-delay-2 mt-4 flex flex-wrap gap-4 text-sm text-soot-300">
              <Link to="/chimneys" className="underline-offset-4 hover:text-white hover:underline">
                Zobacz wszystkich
              </Link>
              {!user && (
                <Link to="/register-chimney" className="underline-offset-4 hover:text-white hover:underline">
                  Dołącz jako kominiarz
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it works – one job */}
      <section className="border-b border-soot-200 bg-soot-50 py-16 md:py-24">
        <div className="ik-container">
          <h2 className="ik-section-title">Jak to działa</h2>
          <p className="ik-section-copy">
            Od wyszukania do przeglądu — bez telefonowania do przypadkowych ogłoszeń.
          </p>
          <ol className="mt-10 grid gap-8 md:grid-cols-3">
            {[
              {
                n: '01',
                title: 'Wyszukaj',
                text: 'Podaj miasto lub wybierz lokalizację z listy. Zobaczysz kominiarzy z Twojej okolicy.',
              },
              {
                n: '02',
                title: 'Umów wizytę',
                text: 'Wybierz termin online. Dostaniesz potwierdzenie i przypomnienia o przeglądzie.',
              },
              {
                n: '03',
                title: 'Pilnuj terminów',
                text: 'Roczne przypomnienie przed kolejnym przeglądem — ważne przy ubezpieczeniu domu.',
              },
            ].map((step) => (
              <li key={step.n} className="border-t border-soot-300 pt-6">
                <p className="font-display text-sm font-semibold tracking-widest text-ember-600">{step.n}</p>
                <h3 className="mt-3 font-display text-xl font-semibold text-soot-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-soot-600 md:text-base">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Cities grid – Fixly-style location picker */}
      <section className="bg-white py-16 md:py-24">
        <div className="ik-container">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="ik-section-title">Kominiarze w miastach</h2>
              <p className="ik-section-copy">
                Wybierz miasto i zobacz fachowców z Twojej okolicy.
              </p>
            </div>
            <Link to="/chimneys" className="ik-btn-secondary self-start md:self-auto">
              Wszystkie miasta
            </Link>
          </div>
          <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3 md:grid-cols-4">
            {POPULAR_CITIES.map((city) => (
              <li key={city} className="border-t border-soot-200">
                <Link
                  to={`/chimneys?city=${encodeURIComponent(city)}`}
                  className="block py-3 text-base font-medium text-soot-800 transition hover:text-ember-600"
                >
                  {city}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Trust strip – no cards */}
      <section className="border-y border-soot-200 bg-soot-100 py-16 md:py-20">
        <div className="ik-container grid gap-10 md:grid-cols-3">
          {[
            {
              title: 'Zweryfikowane wpisy',
              text: 'Baza oparta o dane branżowe i profile, które można przejąć i uzupełnić.',
            },
            {
              title: 'Wizyty w jednym miejscu',
              text: 'Umawiaj, potwierdzaj i śledź status — kominiarz widzi kalendarz, Ty historię.',
            },
            {
              title: 'Przypomnienia o przeglądzie',
              text: 'Miesiąc przed rocznicą ostatniej wizyty dostaniesz sygnał o kolejnym przeglądzie.',
            },
          ].map((item) => (
            <div key={item.title}>
              <h3 className="font-display text-lg font-semibold text-soot-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-soot-600 md:text-base">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-soot-900 py-16 text-white md:py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(194,65,12,0.25),_transparent_55%)]" />
        <div className="ik-container relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-semibold md:text-3xl">Jesteś kominiarzem?</h2>
            <p className="mt-2 max-w-lg text-soot-300">
              Dołącz do platformy, zarządzaj wizytami i bądź widoczny dla klientów szukających przeglądu.
            </p>
          </div>
          <Link to="/register-chimney" className="ik-btn-primary">
            Dołącz do iKominiarz
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;
